import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import React from "react";

import { QueryParams, useQueryParams } from "../../../../shared/hooks/useQueryParams";
import { components, ContractTypeId, OfficeId } from "../../../../shared/schema/schema";

export type RnSchedulingStatus = components["schemas"]["RnSchedulingStatus"];

export type RnSchedulingMapFilters = {
  certificationPeriodEndFrom: LocalDate;
  certificationPeriodEndTo: LocalDate;
  contractTypeIds: ContractTypeId[];
  countyNames: string[];
  officeIds: OfficeId[];
  schedulingStatuses: RnSchedulingStatus[];
};

/**
 * "Next week" - the Monday to Sunday of the week after the current one. This is
 * the range coordinators plan RN assignments for, so it is the default the tab
 * opens with.
 */
export function getNextWeekRange(today: LocalDate = LocalDate.now()): {
  from: LocalDate;
  to: LocalDate;
} {
  const from = today.with(TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY)).plusWeeks(1);

  return { from, to: from.plusDays(6) };
}

function buildDefaultFilters(): RnSchedulingMapFilters {
  const { from, to } = getNextWeekRange();

  return {
    certificationPeriodEndFrom: from,
    certificationPeriodEndTo: to,
    contractTypeIds: [],
    countyNames: [],
    officeIds: [],
    schedulingStatuses: [],
  };
}

const RnSchedulingMapFiltersContext =
  React.createContext<QueryParams<RnSchedulingMapFilters> | null>(null);

export function RnSchedulingMapFiltersProvider({ children }: { children: React.ReactNode }) {
  const filters = useQueryParams<RnSchedulingMapFilters>({
    storageKey: ["rnSchedulingMapFilters"],
    defaultOptions: buildDefaultFilters(),
  });

  return (
    <RnSchedulingMapFiltersContext.Provider value={filters}>
      {children}
    </RnSchedulingMapFiltersContext.Provider>
  );
}

export function useRnSchedulingMapFilters(): QueryParams<RnSchedulingMapFilters> {
  const context = React.useContext(RnSchedulingMapFiltersContext);

  if (context === null) {
    throw new Error(
      "useRnSchedulingMapFilters must be used inside a RnSchedulingMapFiltersProvider"
    );
  }

  return context;
}

/**
 * The filters the endpoint is called with. `resetToDefault` empties the stored
 * params entirely, so the date range falls back to next week here as well.
 */
export function useRnSchedulingMapQueryFilters(): RnSchedulingMapFilters {
  const filters = useRnSchedulingMapFilters();
  const fallback = buildDefaultFilters();

  return {
    certificationPeriodEndFrom:
      filters.getValue("certificationPeriodEndFrom") ?? fallback.certificationPeriodEndFrom,
    certificationPeriodEndTo:
      filters.getValue("certificationPeriodEndTo") ?? fallback.certificationPeriodEndTo,
    contractTypeIds: filters.getValue("contractTypeIds") ?? [],
    countyNames: filters.getValue("countyNames") ?? [],
    officeIds: filters.getValue("officeIds") ?? [],
    schedulingStatuses: filters.getValue("schedulingStatuses") ?? [],
  };
}
